/**
 * frontend/src/components/NearbyMapHero.jsx
 * Home page hero — "what's near me" on a real map.
 *
 * [Sprint UI-4] Direction A from the hero exploration: the user's
 * position and a pin per location with its best discount.
 *
 * Map: MapLibre GL + OpenFreeMap (liberty). No API key, no request
 * limits; the required credit is a text line in the top-left corner. The
 * library is ~250KB gzip, so it is imported lazily — the card paints
 * immediately with a flat background and the map fades in once the chunk
 * and style load.
 *
 * Gestures: mouse drag pans; on phones panning takes two fingers so a
 * one-finger swipe still scrolls the page (MapLibre cooperativeGestures).
 * No rotation. +/− buttons zoom, the locate button restores the first view.
 *
 * Pins: one per location (partners at the same coordinates share it). Pins
 * whose tags would overlap merge into one pin; a number on a pin is how
 * many offers it holds. Tapping a
 * merged pin zooms into it; tapping a single pin selects it — the page
 * moves that organisation's vouchers to the front of the nearby row.
 *
 * States
 *   geoStatus 'denied'   → same card, no map, "ჩართე ლოკაცია" action
 *                          (replaces the old GeoDeniedHint banner)
 *   no coords yet        → flat card, chip skeleton
 *   offers without partner_lat/lng → map + your position, no pins
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LocateFixed, Minus, Plus } from 'lucide-react';

const STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';
// Credit line required by OpenFreeMap and the OpenStreetMap licence.
const MAP_CREDITS = [
  { label: 'OpenFreeMap', href: 'https://openfreemap.org' },
  { label: '© OpenMapTiles', href: 'https://www.openmaptiles.org/' },
  { label: '© OpenStreetMap', href: 'https://www.openstreetmap.org/copyright' },
];
const MIN_ZOOM = 10;
const MAX_ZOOM = 17;
// The hero is about what is actually near: the pins and the map frame
// use this radius (distance_km comes from the backend).
const RADIUS_KM = 5;
const inRadius = (o) => o.distance_km != null && o.distance_km <= RADIUS_KM;
// Two pins closer than this on screen merge (a "-30%" tag is ~44×20px).
const PIN_GAP_X = 50;
const PIN_GAP_Y = 30;
// Screen rectangles of a pin's tag, relative to its tip: the compact tag
// (discount only) and the wide one (discount + organisation name). A pin
// shows the name only where the wide tag would not touch a neighbour.
const COMPACT_RECT = { x0: -26, x1: 26, y0: -36, y1: -12 };
const WIDE_RECT = { x0: -54, x1: 54, y0: -52, y1: -12 };
const rectAt = (r, pt) => ({ x0: pt.x + r.x0, x1: pt.x + r.x1, y0: pt.y + r.y0, y1: pt.y + r.y1 });
const intersects = (a, b) => a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;

// Georgian street names where OSM has them, otherwise the default label.
const LABEL_KA = ['coalesce', ['get', 'name:ka'], ['get', 'name']];
// Hidden layers: place names (city, district, village…) and the style's
// own shop/café icons, which read like competing offer pins.
const HIDDEN_SOURCE_LAYERS = new Set(['place', 'poi']);

const GESTURE_TEXT = {
  'CooperativeGesturesHandler.WindowsHelpText': 'რუკის ზუმისთვის გამოიყენე Ctrl + სქროლი',
  'CooperativeGesturesHandler.MacHelpText': 'რუკის ზუმისთვის გამოიყენე ⌘ + სქროლი',
  'CooperativeGesturesHandler.MobileHelpText': 'რუკის გადასაადგილებლად გამოიყენე ორი თითი',
};

// One entry per location within the radius. Partners at the exact same
// coordinates (same building) share it.
function locations(offers) {
  const byPlace = new Map();
  for (const o of offers || []) {
    const lat = o.partner_lat;
    const lng = o.partner_lng;
    if (typeof lat !== 'number' || typeof lng !== 'number' || !inRadius(o)) continue;
    const id = `${lat.toFixed(5)},${lng.toFixed(5)}`;
    const disc = Math.round(o.discount_percent || 0);
    const prev = byPlace.get(id);
    if (!prev) {
      byPlace.set(id, {
        id, lat, lng, disc, dist: o.distance_km, count: 1,
        partnerIds: new Set([o.partner_id]),
        partnerNames: new Map([[o.partner_id, o.partner_name || 'პარტნიორი']]),
      });
    } else {
      prev.count += 1;
      prev.partnerIds.add(o.partner_id);
      prev.partnerNames.set(o.partner_id, o.partner_name || 'პარტნიორი');
      if (disc > prev.disc) prev.disc = disc;
    }
  }
  return [...byPlace.values()].sort((a, b) => (a.dist ?? Infinity) - (b.dist ?? Infinity));
}

// Tag: discount on the first line, organisation name on the second
// (when there is room for it — see draw()).
const TAG_OFF = 'bg-white text-slate-900 leading-none px-2.5 py-[5px] shadow-[0_2px_6px_rgba(15,23,42,0.14)]';
const TAG_ON = 'bg-[#b21a1a] text-white leading-none px-2.5 py-[5px] shadow-[0_3px_10px_rgba(178,26,26,0.35)]';
const STEM_OFF = 'w-[2px] h-[6px] bg-white';
const STEM_ON = 'w-[2px] h-[6px] bg-[#b21a1a]';
const BADGE = 'absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-bold leading-[18px] text-center ring-2 ring-white';
// Names are clipped so a long one cannot cover half the map.
const NAME = 'block max-w-[112px] mt-[3px] text-[10px] font-medium leading-none truncate';

const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

// The tag is the visible part; the padding around it widens the tap target.
function pinElement({ disc, count, name, selected, label, onTap }) {
  const el = document.createElement('button');
  el.type = 'button';
  el.setAttribute('aria-label', label);
  el.className = 'flex flex-col items-center px-2 pt-3 cursor-pointer';
  el.style.zIndex = selected ? '2' : '';
  el.innerHTML =
    `<div class="relative flex flex-col items-center ${name ? 'rounded-xl' : 'rounded-full'} ${selected ? TAG_ON : TAG_OFF}">` +
    `<span class="text-[11.5px] font-bold ${selected ? '' : 'text-[#b21a1a]'}">-${disc}%</span>` +
    (name ? `<span class="${NAME} ${selected ? 'text-white/90' : 'text-slate-600'}">${escapeHtml(name)}</span>` : '') +
    (count > 1 ? `<span class="${BADGE}">${count}</span>` : '') +
    '</div>' +
    `<div class="${selected ? STEM_ON : STEM_OFF}"></div>` +
    '<div class="w-2 h-2 rounded-full bg-[#b21a1a] border-2 border-white"></div>';
  // Stop the tap reaching the map, whose click clears the selection.
  el.addEventListener('click', (e) => {
    e.stopPropagation();
    onTap();
  });
  return el;
}

function youElement() {
  const el = document.createElement('div');
  el.className = 'relative w-8 h-8 flex items-center justify-center pointer-events-none';
  el.innerHTML =
    '<span class="absolute inset-0 rounded-full bg-blue-600/15"></span>' +
    '<span class="relative w-3.5 h-3.5 rounded-full bg-blue-600 border-[3px] border-white shadow"></span>';
  return el;
}

const ctrlBtn = 'w-9 h-9 flex items-center justify-center text-slate-700 active:bg-slate-100 disabled:text-slate-300';

export default function NearbyMapHero({ geoStatus, coords, offers, onSelectPartners, onEnableLocation }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const libRef = useRef(null);
  const youRef = useRef(null);
  const pinMarkersRef = useRef([]);
  const [mapReady, setMapReady] = useState(false);
  const [mapFailed, setMapFailed] = useState(false);
  const [selectedId, setSelectedId] = useState(null);   // location id
  const [zoom, setZoom] = useState(null);

  const denied = geoStatus === 'denied';
  const places = useMemo(() => locations(offers), [offers]);
  const hasCoords = !!coords;
  const lat = coords?.lat;
  const lng = coords?.lng;

  // Create the map once coordinates exist.
  useEffect(() => {
    if (denied || !hasCoords || mapRef.current || !containerRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const [mod] = await Promise.all([
          import('maplibre-gl'),
          import('maplibre-gl/dist/maplibre-gl.css'),
        ]);
        if (cancelled || !containerRef.current) return;
        const maplibregl = mod.default || mod;
        libRef.current = maplibregl;
        const map = new maplibregl.Map({
          container: containerRef.current,
          style: STYLE_URL,
          center: [lng, lat],
          zoom: 13,
          minZoom: MIN_ZOOM,
          maxZoom: MAX_ZOOM,
          dragRotate: false,
          pitchWithRotate: false,
          touchPitch: false,
          cooperativeGestures: true,
          locale: GESTURE_TEXT,
          // No MapLibre attribution control — its "i" button read as an
          // unexplained control. The credit is a text line (MAP_CREDITS).
          attributionControl: false,
          fadeDuration: 0,
        });
        map.touchZoomRotate.disableRotation();
        map.keyboard.disableRotation();
        mapRef.current = map;
        let loaded = false;
        map.on('load', () => {
          loaded = true;
          if (cancelled) return;
          for (const layer of map.getStyle().layers || []) {
            if (HIDDEN_SOURCE_LAYERS.has(layer['source-layer'])) {
              map.setLayoutProperty(layer.id, 'visibility', 'none');
            } else if (layer.type === 'symbol' && layer.layout && layer.layout['text-field']) {
              map.setLayoutProperty(layer.id, 'text-field', LABEL_KA);
            }
          }
          setMapReady(true);
        });
        map.on('error', () => {
          // A tile hiccup after load is routine; an error before the style
          // ever loaded means no map — fall back to the flat card.
          if (!loaded && !cancelled) setMapFailed(true);
        });
      } catch {
        if (!cancelled) setMapFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [denied, hasCoords, lat, lng]);

  // Tear down on unmount.
  useEffect(() => () => {
    pinMarkersRef.current.forEach((m) => m.remove());
    youRef.current?.remove();
    mapRef.current?.remove();
    mapRef.current = null;
  }, []);

  // The first view: you plus every location within the radius.
  const fitView = useCallback((animate) => {
    const map = mapRef.current;
    const maplibregl = libRef.current;
    if (!map || !maplibregl || lat == null) return;
    if (places.length) {
      const bounds = new maplibregl.LngLatBounds([lng, lat], [lng, lat]);
      places.forEach((p) => bounds.extend([p.lng, p.lat]));
      // Padding keeps pins clear of the credit line, the buttons and the chip.
      map.fitBounds(bounds, {
        padding: { top: 76, bottom: 36, left: 44, right: 64 },
        maxZoom: 15,
        duration: animate ? 400 : 0,
      });
    } else {
      map.easeTo({ center: [lng, lat], zoom: 13, duration: animate ? 400 : 0 });
    }
  }, [places, lat, lng]);

  // "You" marker + first view. Re-runs when the user moves or offers change.
  useEffect(() => {
    const map = mapRef.current;
    const maplibregl = libRef.current;
    if (!mapReady || !map || !maplibregl || !hasCoords) return;
    youRef.current?.remove();
    youRef.current = new maplibregl.Marker({ element: youElement() }).setLngLat([lng, lat]).addTo(map);
    map.resize();
    fitView(false);
    setZoom(map.getZoom());
  }, [mapReady, hasCoords, lat, lng, fitView]);

  // Pins. Rebuilt after every move/zoom: locations whose tags would
  // overlap on screen merge into one pin (nearest-first), so nothing
  // disappears — it folds into a neighbour and its count shows on it.
  useEffect(() => {
    const map = mapRef.current;
    const maplibregl = libRef.current;
    if (!mapReady || !map || !maplibregl) return;

    const draw = () => {
      pinMarkersRef.current.forEach((m) => m.remove());
      const groups = [];
      const ordered = [...places].sort((a, b) => (b.id === selectedId) - (a.id === selectedId));
      for (const p of ordered) {
        const pt = map.project([p.lng, p.lat]);
        // The selected location always keeps a pin of its own.
        const g = p.id === selectedId ? null : groups.find((q) =>
          q.members[0].id !== selectedId &&
          Math.abs(q.pt.x - pt.x) < PIN_GAP_X && Math.abs(q.pt.y - pt.y) < PIN_GAP_Y);
        if (g) g.members.push(p);
        else groups.push({ pt, members: [p] });
      }

      // Names, nearest-first: a group keeps its name only if the wide tag
      // clears every neighbour (their wide tag if named, else compact).
      // The selected pin always keeps its name.
      const taken = [];
      groups.forEach((g) => { g.named = false; });
      for (const g of groups) {
        const wide = rectAt(WIDE_RECT, g.pt);
        const clear = groups.every((o) => o === g ||
          !intersects(wide, rectAt(o.named ? WIDE_RECT : COMPACT_RECT, o.pt)));
        g.named = g.members[0].id === selectedId || (clear && !taken.some((t) => intersects(wide, t)));
        if (g.named) taken.push(wide);
      }

      pinMarkersRef.current = groups.map(({ members, named }) => {
        const head = members[0];
        const count = members.reduce((n, m) => n + m.count, 0);
        const disc = Math.max(...members.map((m) => m.disc));
        const merged = members.length > 1;
        // One organisation → its name; several (same building or merged
        // on screen) → how many there are.
        const names = new Map();
        members.forEach((m) => m.partnerNames.forEach((v, k) => names.set(k, v)));
        const name = names.size === 1 ? [...names.values()][0] : `${names.size} ორგანიზაცია`;
        const onTap = merged
          ? () => {
              // Zoom into the merged locations until they separate.
              const b = new maplibregl.LngLatBounds([head.lng, head.lat], [head.lng, head.lat]);
              members.forEach((m) => b.extend([m.lng, m.lat]));
              map.fitBounds(b, { padding: 70, maxZoom: MAX_ZOOM, duration: 400 });
            }
          : () => setSelectedId((cur) => (cur === head.id ? null : head.id));
        const el = pinElement({
          disc,
          count,
          name: named ? name : null,
          selected: !merged && head.id === selectedId,
          label: `${name}: ${count} შეთავაზება, ფასდაკლება ${disc}%-მდე`,
          onTap,
        });
        return new maplibregl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([head.lng, head.lat])
          .addTo(map);
      });
    };

    draw();
    const onMoveEnd = () => { setZoom(map.getZoom()); draw(); };
    map.on('moveend', onMoveEnd);
    return () => { map.off('moveend', onMoveEnd); };
  }, [mapReady, places, selectedId]);

  // Tapping the map itself (not a pin) clears the selection. MapLibre's
  // click does not fire after a drag, unlike a DOM click on the wrapper.
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;
    const onClick = () => setSelectedId(null);
    map.on('click', onClick);
    return () => { map.off('click', onClick); };
  }, [mapReady]);

  const selectedPlace = places.find((p) => p.id === selectedId);

  // Drop a selection whose location is no longer in the list.
  useEffect(() => {
    if (selectedId && offers && !selectedPlace) setSelectedId(null);
  }, [selectedId, offers, selectedPlace]);

  // Tell the page which partners are selected; it reorders its own
  // voucher row (no second row under the map).
  useEffect(() => {
    onSelectPartners?.(selectedPlace ? [...selectedPlace.partnerIds] : null);
  }, [selectedPlace, onSelectPartners]);

  const zoomBy = (delta) => () => {
    const map = mapRef.current;
    if (!map) return;
    if (delta > 0) map.zoomIn({ duration: 250 });
    else map.zoomOut({ duration: 250 });
  };

  const showMap = !denied && !mapFailed;

  return (
    <section className="mt-1" data-testid="nearby-map-hero">
      <div
        {...(denied ? {
          role: 'button',
          tabIndex: 0,
          onClick: onEnableLocation,
          onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEnableLocation?.(); } },
        } : {})}
        className={`relative ${denied ? 'h-[112px] cursor-pointer active:scale-[0.99] transition-transform' : 'h-[180px] md:h-[220px]'} rounded-[20px] overflow-hidden bg-[#eef3fb] border border-slate-200`}
      >
        {showMap && (
          <div
            ref={containerRef}
            className={`absolute inset-0 transition-opacity duration-300 ${mapReady ? 'opacity-100' : 'opacity-0'}`}
          />
        )}

        {showMap && (
          <div className="absolute left-2 top-1.5 px-1 rounded bg-white/70 text-[9px] leading-[14px] text-slate-500 whitespace-nowrap">
            {MAP_CREDITS.map((c, i) => (
              <React.Fragment key={c.href}>
                {i > 0 && ' · '}
                <a href={c.href} target="_blank" rel="noopener noreferrer" className="hover:text-slate-700">
                  {c.label}
                </a>
              </React.Fragment>
            ))}
          </div>
        )}

        {showMap && mapReady && (
          <div className="absolute right-2.5 top-2.5 flex flex-col gap-2">
            <div className="flex flex-col rounded-xl bg-white/95 shadow-[0_1px_4px_rgba(15,23,42,0.14)] overflow-hidden">
              <button
                type="button"
                onClick={zoomBy(1)}
                disabled={zoom != null && zoom >= MAX_ZOOM - 0.01}
                className={ctrlBtn}
                aria-label="გადიდება"
              >
                <Plus className="w-4 h-4" strokeWidth={2.2} />
              </button>
              <div className="h-px bg-slate-200 mx-1.5" />
              <button
                type="button"
                onClick={zoomBy(-1)}
                disabled={zoom != null && zoom <= MIN_ZOOM + 0.01}
                className={ctrlBtn}
                aria-label="შემცირება"
              >
                <Minus className="w-4 h-4" strokeWidth={2.2} />
              </button>
            </div>
            <button
              type="button"
              onClick={() => fitView(true)}
              className={`${ctrlBtn} rounded-xl bg-white/95 shadow-[0_1px_4px_rgba(15,23,42,0.14)]`}
              aria-label="ცენტრში დაბრუნება"
            >
              <LocateFixed className="w-4 h-4 text-blue-600" strokeWidth={2.2} />
            </button>
          </div>
        )}

        {denied ? (
          <div className="absolute left-3 right-3 bottom-3 bg-white rounded-2xl pl-4 pr-3.5 py-3.5 shadow-[0_4px_16px_rgba(15,23,42,0.10)] flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[17px] font-bold tracking-tight text-slate-900 leading-tight">ჩართე ლოკაცია</div>
              <div className="text-[12.5px] text-slate-500 leading-snug mt-0.5">ნახე ფასდაკლებები შენ ახლოს</div>
            </div>
            <span className="h-11 px-4 rounded-full bg-blue-600 text-white text-[13px] font-semibold flex items-center gap-1.5 shrink-0">
              <LocateFixed className="w-4 h-4" strokeWidth={2.2} />
              ჩართვა
            </span>
          </div>
        ) : (
          null
        )}
      </div>

    </section>
  );
}
